import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  FileUpload,
  Form,
  FormField,
  Input,
  Modal,
  Multiselect,
  SpaceBetween,
  Textarea,
} from "@amzn/awsui-components-react/polaris";

import { createDashboard, updateDashboard, fetchAllApps, uploadLogo } from "./api";
import { createAuditLog } from "../Templates/Utils/AppActivityUtils";

/**
 * Create / Edit modal for a Task Manager dashboard.
 *
 * When `dashboard` is provided the modal runs in edit mode: fields are
 * prepopulated from it, the primary action saves via updateDashboard, and the
 * parent is notified through onSaved. Otherwise it creates a new dashboard and
 * notifies through onCreated.
 *
 * Editing is a privileged action; the page only opens this in edit mode for
 * portal / support admins.
 */
const CreateTaskManagerModal = ({ visible, onDismiss, onCreated, onSaved, dashboard }) => {
  const isEdit = Boolean(dashboard && dashboard.dashboard_id);

  const userId = useSelector((globalState) => globalState.auth.userId);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedApps, setSelectedApps] = useState([]);
  const [appOptions, setAppOptions] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [logoFiles, setLogoFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Load the apps for the multiselect when the modal opens.
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setAppsLoading(true);
    fetchAllApps()
      .then((apps) => {
        if (cancelled) return;
        setAppOptions(
          (apps || []).map((app) => ({
            label: app.app_name,
            value: String(app.app_id),
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setAppOptions([]);
      })
      .finally(() => {
        if (!cancelled) setAppsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  // Prepopulate (edit) or clear (create) the form whenever the modal opens.
  useEffect(() => {
    if (!visible) return;
    if (isEdit) {
      setName(dashboard.name || "");
      setDescription(dashboard.description || "");
    } else {
      setName("");
      setDescription("");
      setSelectedApps([]);
    }
    setLogoFiles([]);
    setError("");
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, dashboard]);

  // Once the app options are loaded in edit mode, map the dashboard's app_ids
  // to selected options (so tokens show the app names).
  useEffect(() => {
    if (!visible || !isEdit || appOptions.length === 0) return;
    const ids = (dashboard.app_ids || []).map((id) => String(id));
    setSelectedApps(appOptions.filter((o) => ids.includes(o.value)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isEdit, appOptions, dashboard]);

  const reset = () => {
    setName("");
    setDescription("");
    setSelectedApps([]);
    setLogoFiles([]);
    setError("");
    setSubmitting(false);
  };

  const handleDismiss = () => {
    if (submitting) return;
    reset();
    onDismiss();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      // Upload the logo (if a new one was chosen) through Vault first; persist
      // the returned S3 object key. In edit mode, keep the existing logo when
      // no new file is chosen.
      let logo = isEdit ? dashboard.logo || "" : "";
      if (logoFiles.length > 0) {
        try {
          logo = await uploadLogo(logoFiles[0]);
        } catch (uploadErr) {
          setError(uploadErr?.message || "Logo upload failed");
          setSubmitting(false);
          return;
        }
      }

      const payload = {
        name: name.trim(),
        description: description.trim(),
        logo,
        app_ids: selectedApps.map((o) => Number(o.value)),
      };

      // Audit "before": snapshot the prior state (the existing dashboard on
      // edit, empty on create) and the requested change, before applying it.
      const auditPk = `TASK_MANAGER#${isEdit ? dashboard.dashboard_id : "NEW"}`;
      const beforeState = isEdit ? dashboard : {};
      createAuditLog(
        auditPk,
        `${isEdit ? "UPDATE" : "CREATE"}_REQ#${Date.now()}`,
        `Task Manager ${isEdit ? "update" : "create"} requested`,
        JSON.stringify(beforeState),
        JSON.stringify(payload),
        isEdit ? "TASK MANAGER UPDATE REQ" : "TASK MANAGER CREATE REQ",
        userId
      );

      let result;
      if (isEdit) {
        result = await updateDashboard({ dashboard_id: dashboard.dashboard_id, ...payload });
      } else {
        result = await createDashboard(payload);
      }

      if (result && result.error) {
        setError(result.error);
        setSubmitting(false);
        return;
      }

      // Audit "after": record the applied result once the change succeeded.
      createAuditLog(
        `TASK_MANAGER#${result?.dashboard_id ?? (isEdit ? dashboard.dashboard_id : "NEW")}`,
        `${isEdit ? "UPDATE" : "CREATE"}#${Date.now()}`,
        `Task Manager ${isEdit ? "updated" : "created"}`,
        JSON.stringify(beforeState),
        JSON.stringify(result),
        isEdit ? "TASK MANAGER UPDATED" : "TASK MANAGER CREATED",
        userId
      );

      if (isEdit) {
        if (onSaved) onSaved(result);
      } else if (onCreated) {
        onCreated(result);
      }
      reset();
      onDismiss();
    } catch (e) {
      setError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={isEdit ? "Edit Task Manager" : "Create Task Manager"}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!name.trim()}
            >
              {isEdit ? "Save changes" : "Create"}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form errorText={error}>
        <SpaceBetween direction="vertical" size="l">
          <FormField label="Name">
            <Input
              value={name}
              placeholder="Enter a name"
              onChange={({ detail }) => setName(detail.value)}
            />
          </FormField>
          <FormField label="Description" description="Optional">
            <Textarea
              value={description}
              placeholder="What is this task manager for?"
              onChange={({ detail }) => setDescription(detail.value)}
            />
          </FormField>
          <FormField label="Include Apps">
            <Multiselect
              selectedOptions={selectedApps}
              options={appOptions}
              onChange={({ detail }) => setSelectedApps(detail.selectedOptions)}
              filteringType="auto"
              statusType={appsLoading ? "loading" : "finished"}
              loadingText="Loading apps"
              placeholder="Select apps to include..."
              tokenLimit={5}
              empty="No apps available"
            />
          </FormField>
          <FormField
            label="Logo"
            description={isEdit ? "Optional. Upload to replace the current logo." : "Optional. PNG, JPG, SVG or GIF."}
          >
            <FileUpload
              value={logoFiles}
              onChange={({ detail }) => setLogoFiles(detail.value)}
              accept="image/png,image/jpeg,image/svg+xml,image/gif"
              showFileLastModified
              showFileSize
              constraintText="Upload a single image to use as the task manager logo."
              i18nStrings={{
                uploadButtonText: (multiple) => (multiple ? "Choose files" : "Choose file"),
                dropzoneText: (multiple) => (multiple ? "Drop files to upload" : "Drop file to upload"),
                removeFileAriaLabel: (index) => `Remove file ${index + 1}`,
                limitShowFewer: "Show fewer files",
                limitShowMore: "Show more files",
                errorIconAriaLabel: "Error",
              }}
            />
          </FormField>
        </SpaceBetween>
      </Form>
    </Modal>
  );
};

export default CreateTaskManagerModal;
